<template>
  <div class="btn-toolbar">
    <b-button-group>
      <b-button class="txt-btn" variant="outline-primary" @click="handleExport">
        <font-awesome-icon :icon="['fas', 'upload']" /> Exporter
      </b-button>
    </b-button-group>
    <b-button-group>
      <b-button
        v-if="!this.isSaving()"
        @click.prevent="saveChanges()"
        class="txt-btn"
        variant="outline-primary"
      >
        <font-awesome-icon :icon="['fas', 'save']" /> Sauvegarder
      </b-button>
      <b-button v-else class="txt-btn" variant="primary">
        <b-spinner small type="grow"></b-spinner> En attente...
      </b-button>
    </b-button-group>
    <div class="divider"></div>
    <b-button-group>
      <b-dropdown
        :style="[
          {
            fontFamily: this.getSelectedFontFamily().family,
            fontSize: '12px',
            textOverflow: 'ellipsis'
          }
        ]"
        variant="outline-primary"
        class="font-type"
      >
        <template slot="button-content">
          {{ this.getSelectedFontFamily().name | truncate(10) }}
        </template>
        <b-dropdown-item
          v-for="fm in this.fontFamily"
          :key="fm.name"
          :active="fm.state"
          :style="[{ fontFamily: fm.family, fontSize: '11px' }]"
          @click.prevent="fontFamilyClick(fm.family)"
          >{{ fm.name }}</b-dropdown-item
        >
      </b-dropdown>
      <b-dropdown
        :text="this.getSelectedFontSize()"
        variant="outline-primary"
        class="font-size"
      >
        <b-dropdown-item
          v-for="size in this.fontSize"
          :key="size.value"
          :active="size.state"
          :style="[{ fontFamily: 'Arial', fontSize: '11px' }]"
          @click.prevent="fontSizeSelected(size.value)"
          >{{ size.value }}</b-dropdown-item
        >
      </b-dropdown>
    </b-button-group>
    <b-button-group>
      <b-button
        :pressed="this.fontWeight.state"
        @click.prevent="fontWeightClick()"
        class="icon-btn"
        variant="outline-primary"
      >
        <font-awesome-icon :icon="['fas', 'bold']" />
      </b-button>
      <b-button
        :pressed="this.fontStyle.state"
        @click.prevent="fontStyleClick()"
        class="icon-btn"
        variant="outline-primary"
      >
        <font-awesome-icon :icon="['fas', 'italic']" />
      </b-button>
    </b-button-group>
    <b-button-group>
      <b-button
        class="icon-btn"
        @click.prevent="exposantClick"
        variant="outline-primary"
      >
        <font-awesome-icon :icon="['fas', 'superscript']" />
      </b-button>
      <b-button
        class="icon-btn"
        @click.prevent="indiceClick"
        variant="outline-primary"
      >
        <font-awesome-icon :icon="['fas', 'subscript']" />
      </b-button>
    </b-button-group>
    <b-button-group>
      <b-dropdown
        id="font-color-picker"
        class="icon-btn"
        variant="outline-primary"
      >
        <template slot="button-content">
          <font-awesome-icon v-bind:style="{ color }" :icon="['fas', 'font']" />
        </template>
        <div class="color-picker">
          <color-picker
            v-if="showFontColorPicker"
            :sucker-hide="true"
            :color="color"
            @changeColor="changeColor"
          />
        </div>
      </b-dropdown>
      <b-dropdown
        id="background-color-picker"
        class="icon-btn"
        variant="outline-primary"
      >
        <template slot="button-content">
          <font-awesome-icon
            v-bind:style="{ backgroundColor }"
            :icon="['fas', 'fill-drip']"
          />
        </template>
        <div class="color-picker">
          <color-picker
            v-if="showBackgroundColorPicker"
            :sucker-hide="true"
            :color="backgroundColor"
            colors-history-key="background-color-history"
            @changeColor="changeBackgroundColor"
          />
        </div>
      </b-dropdown>
    </b-button-group>
    <b-button-group>
      <b-button
        v-b-toggle.border-picker
        variant="outline-primary"
        class="icon-btn"
      >
        <icon-base
          :icon-name="'border-all'"
          width="18"
          height="18"
          style="margin-bottom: -4px"
        />
      </b-button>
      <b-collapse id="border-picker" class="mt-1">
        <div class="flex-container flex-column d-flex py-2 px-2">
          <div class="d-flex">
            <div
              class="flex-item"
              v-for="border in this.borders"
              :key="border.type"
              @click.prevent="borderTypeClick(border)"
            >
              <icon-base
                style="margin-bottom: 0;"
                :icon-name="border.icon"
                width="18"
                height="18"
              />
            </div>
          </div>
          <div
            class="d-flex my-2"
            style="border-top: 2px solid lightgray;"
          ></div>
          <div class="d-flex">
            <div class="flex-fill action-container">
              <div class="flex-action" v-b-toggle.line-weight>
                <icon-base
                  style="margin-bottom: 0;"
                  icon-name="line-weight"
                  width="18"
                  height="18"
                />
                <font-awesome-icon :icon="['fas', 'caret-down']" size="xs" />
              </div>
              <b-collapse id="line-weight">
                <div class="flex-container d-flex flex-column py-2">
                  <div
                    v-for="(size, index) in this.borderWidth"
                    :key="`size-${index}`"
                    class="border-width-item my-1 p-2"
                    :class="{ active: size.state }"
                    @click="borderWidthClick(size)"
                  >
                    <div
                      class="line-display"
                      :style="{ height: size.value }"
                    ></div>
                  </div>
                </div>
              </b-collapse>
            </div>
            <div style="border-right: 1px solid lightgray;"></div>
            <div class="flex-fill action-container">
              <div class="flex-action" v-b-toggle.border-color>
                <icon-base
                  style="margin-bottom: 0;"
                  icon-name="border-color"
                  width="18"
                  height="18"
                />
                <font-awesome-icon :icon="['fas', 'caret-down']" size="xs" />
              </div>
              <b-collapse id="border-color">
                <div class="flex-container d-flex flex-column py-2">
                  <div class="color-picker">
                    <color-picker
                      v-if="showBorderColorPicker"
                      :sucker-hide="true"
                      :color="borderColor"
                      colors-history-key="background-color-history"
                      @changeColor="changeBorderColor"
                    />
                  </div>
                </div>
              </b-collapse>
            </div>
          </div>
        </div>
      </b-collapse>
    </b-button-group>
    <b-button-group>
      <b-button
        v-for="align in this.justifyContent"
        :key="align.type"
        @click.prevent="textAlignClick(align.type)"
        :pressed="align.state"
        class="icon-btn"
        variant="outline-primary"
      >
        <font-awesome-icon :icon="['fas', align.icon]" />
      </b-button>
    </b-button-group>
    <div class="divider"></div>
    <b-button-group>
      <b-button class="icon-btn" variant="outline-primary">
        <font-awesome-icon :icon="['fas', 'filter']" />
      </b-button>
      <b-button
        @click.prevent="removeStyle()"
        class="icon-btn"
        variant="outline-primary"
      >
        <icon-base
          style="margin-bottom: -4px;"
          icon-name="format-clear"
          width="18"
          height="18"
        />
      </b-button>
    </b-button-group>
    <div class="divider"></div>
    <b-button-group>
      <b-button
        @click.prevent="configFormatDonnees()"
        class="icon-btn"
        variant="outline-primary"
      >
        Configurer le format de donnée
      </b-button>
    </b-button-group>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { fontSizes } from '../../config/constanteUse'
import { removeHtmlTag } from '../../utils/util'
import colorPicker from '@caohenghu/vue-colorpicker'
import iconBase from '../icons/iconBase'
export default {
  components: {
    colorPicker,
    iconBase
  },
  data () {
    return {
      data: {},
      borderPickerShown: false,
      context: null,
      justifyContent: [
        { icon: 'align-left', type: 'flex-start', state: false },
        { icon: 'align-center', type: 'center', state: false },
        { icon: 'align-right', type: 'flex-end', state: false }
      ],
      fontStyle: {
        name: 'italic', state: false
      },
      fontWeight: {
        name: 'bold', state: false
      },
      fontFamily: [
        { name: 'Arial', family: 'Arial, Helvetica, sans-serif', state: true },
        { name: 'Arial Black', family: '"Arial Black", Gadget, sans-serif', state: false },
        { name: 'Comic Sans MS', family: '"Comic Sans MS", cursive, sans-serif', state: false },
        { name: 'Courier New', family: '"Courier New", Courier, monospace', state: false },
        { name: 'Georgia', family: 'Georgia, serif', state: false },
        { name: 'Lucida Console', family: '"Lucida Console", Monaco, monospace', state: false },
        { name: 'Lucida Sans Unicode', family: '"Lucida Sans Unicode", "Lucida Grande", sans-serif', state: false },
        { name: 'Open Sans', family: '"Open sans", Arial, sans-serif', state: false },
        { name: 'Palatino Linotype', family: '"Palatino Linotype", "Book Antiqua", Palatino, serif', state: false },
        { name: 'Tahoma', family: 'Tahoma, Geneva, sans-serif', state: false },
        { name: 'Times New Roman', family: '"Times New Roman", Times, serif', state: false },
        { name: 'Trebuchet MS', family: '"Trebuchet MS", Helvetica, sans-serif', state: false },
        { name: 'Verdana', family: 'Verdana, Geneva, sans-serif', state: false }
      ],
      borders: [
        {
          type: 'none', size: '0', style: 'solid', icon: 'border-none'
        },
        {
          type: 'top', size: '2px', style: 'solid', icon: 'border-top'
        },
        {
          type: 'right', size: '2px', style: 'solid', icon: 'border-right'
        },
        {
          type: 'bottom', size: '2px', style: 'solid', icon: 'border-bottom'
        },
        {
          type: 'left', size: '2px', style: 'solid', icon: 'border-left'
        },
        {
          type: 'all', size: '2px', style: 'solid', icon: 'border-all'
        },
        {
          type: 'outer', size: '2px', style: 'solid', icon: 'border-outer'
        }
      ],
      borderWidth: [
        {
          value: '0.125rem', state: true
        },
        {
          value: '0.1875rem', state: false
        },
        {
          value: '0.250rem', state: false
        },
        {
          value: '0.3125rem', state: false
        }
      ],
      fontSize: [],
      showFontColorPicker: false,
      showBackgroundColorPicker: false,
      showBorderColorPicker: false,
      color: '',
      backgroundColor: '',
      borderColor: '',
      isSelectingColor: false,
      colorPickerDelay: null
    }
  },
  created () {
    this.fontSize = fontSizes.map(size => {
      return {
        value: size,
        state: false
      }
    })
    if (!this.activeBorderWidth()) {
      this.setActiveBorderWidth()
    }
    if (!this.activeBorderColor()) {
      this.setActiveBorderColor()
    }
  },
  mounted () {
    this.$root.$on('bv::dropdown::show', bvEvent => {
      if (bvEvent.vueTarget.id === 'font-color-picker') {
        this.showFontColorPicker = true
      } else if (bvEvent.vueTarget.id === 'background-color-picker') {
        this.showBackgroundColorPicker = true
      }
    })
    this.$root.$on('bv::dropdown::hide', bvEvent => {
      if (bvEvent.vueTarget.id === 'font-color-picker') {
        this.showFontColorPicker = false
      } else if (bvEvent.vueTarget.id === 'background-color-picker') {
        this.showBackgroundColorPicker = false
      }
    })
    this.$root.$on('bv::collapse::state', (collapseId, isJustShown) => {
      if (collapseId === 'border-color') {
        this.showBorderColorPicker = isJustShown
      }
    })
  },
  filters: {
    truncate: function (text, length) {
      return (text.length > length) ? text.substring(0, length) + ' ...' : text
    }
  },
  methods: {
    ...mapActions(['updateSelectedCellsBorder', 'updateSelectedCellsStyle', 'setSelectedHeaderData', 'updateSelectedCellValue', 'saveTableauSurface', 'removeSelectedCellsStyle', 'setShowType', 'setActiveBorderWidth', 'setActiveBorderColor']),
    ...mapGetters(['showType', 'isSaving', 'activeBorderWidth', 'activeBorderColor']),
    toggleCollapse () {
      this.$root.$emit('bv::toggle::collapse', 'border-picker')
    },
    getSelectedFontFamily () {
      return this.fontFamily.find(el => el.state === true)
    },
    getSelectedFontSize () {
      return this.selectedCellData.style.fontSize.replace('px', '')
    },
    textAlignClick (type) {
      this.updateStyle('justifyContent', type)
    },
    fontSizeSelected (value) {
      this.updateStyle('fontSize', value, 'px')
    },
    fontFamilyClick (value) {
      this.updateStyle('fontFamily', value)
    },
    borderWidthClick (size) {
      this.setActiveBorderWidth(size.value)
      this.$root.$emit('bv::toggle::collapse', 'line-weight')
    },
    fontWeightClick () {
      const state = !this.fontWeight.state
      if (state) {
        this.updateStyle('fontWeight', 'bold')
      } else {
        this.updateStyle('fontWeight', 'normal')
      }
    },
    fontStyleClick () {
      const state = !this.fontStyle.state
      if (state) {
        this.updateStyle('fontStyle', 'italic')
      } else {
        this.updateStyle('fontStyle', 'normal')
      }
    },
    getSelectionText () {
      let text = ''
      if (window.getSelection) {
        text = window.getSelection().toString()
      } else if (document.selection && document.selection.type !== 'Control') {
        text = document.selection.createRange().text
      }
      return text
    },
    exposantClick () {
      const selected = this.getSelectionText()
      if (selected !== '') {
        if (this.data.hasOwnProperty('valeur')) {
          this.data.valeur = removeHtmlTag(this.data.valeur).replace(selected, `<sup>${selected}</sup>`)
          const { valeur, col, lig } = this.data
          this.updateSelectedCellValue({ valeur, col, lig })
          return
        }
        this.data.titre = removeHtmlTag(this.data.titre).replace(selected, `<sup>${selected}</sup>`)
        const { titre, col, lig } = this.data
        this.updateSelectedCellValue({ titre, col, lig })
      } else {
        if (this.data.hasOwnProperty('valeur')) {
          this.data.valeur = removeHtmlTag(this.data.valeur)
          const { valeur, col, lig } = this.data
          this.updateSelectedCellValue({ valeur, col, lig })
          return
        }
        this.data.titre = removeHtmlTag(this.data.titre)
        const { titre, col, lig } = this.data
        this.updateSelectedCellValue({ titre, col, lig })
      }
    },
    indiceClick () {
      const selected = this.getSelectionText()
      if (selected !== '') {
        if (this.data.hasOwnProperty('valeur')) {
          this.data.valeur = removeHtmlTag(this.data.valeur).replace(selected, `<sub>${selected}</sub>`)
          const { valeur, col, lig } = this.data
          this.updateSelectedCellValue({ valeur, col, lig })
          return
        }
        this.data.titre = removeHtmlTag(this.data.titre).replace(selected, `<sub>${selected}</sub>`)
        const { titre, col, lig } = this.data
        this.updateSelectedCellValue({ titre, col, lig })
      } else {
        if (this.data.hasOwnProperty('valeur')) {
          this.data.valeur = removeHtmlTag(this.data.valeur)
          const { valeur, col, lig } = this.data
          this.updateSelectedCellValue({ valeur, col, lig })
          return
        }
        this.data.titre = removeHtmlTag(this.data.titre)
        const { titre, col, lig } = this.data
        this.updateSelectedCellValue({ titre, col, lig })
      }
    },
    changeColor (color) {
      this.data.style.color = color.rgba.toRgbaString()
      this.updateSelectedCellsStyle(this.data.style)
      /* this.colorPickerDelay = setTimeout(() => {
        EventBus.$emit(events.STYLE_EDIT)
      }, 2000) */
    },
    changeBackgroundColor (backgroundColor) {
      this.data.style.backgroundColor = backgroundColor.rgba.toRgbaString()
      this.updateSelectedCellsStyle(this.data.style)
      /* this.colorPickerDelay = setTimeout(() => {
        EventBus.$emit(events.STYLE_EDIT)
      }, 2000) */
    },
    changeBorderColor (borderColor) {
      this.setActiveBorderColor(borderColor.rgba.toRgbaString())
    },
    borderTypeClick (border) {
      this.updateSelectedCellsBorder(border)
    },
    updateStyle (attribut, value, unit = '') {
      this.data.style[attribut] = `${value}${unit}`
      this.updateSelectedCellsStyle(this.data.style)
    },
    removeStyle () {
      this.removeSelectedCellsStyle()
    },
    syncStateData (newValue) {
      this.data = { ...newValue }
      this.justifyContent.forEach(el => {
        if (this.data.style.justifyContent === el.type) {
          el.state = true
          return
        }
        el.state = false
      })
      this.fontSize.forEach(el => {
        if (this.data.style.fontSize.replace('px', '') === el.value) {
          el.state = true
          return
        }
        el.state = false
      })
      this.fontFamily.forEach(el => {
        if (this.data.style.fontFamily === el.family) {
          el.state = true
          return
        }
        el.state = false
      })
      this.borderWidth.forEach(el => {
        if (this.activeBorderWidth() === el.value) {
          el.state = true
          return
        }
        el.state = false
      })
      this.fontStyle.state = this.data.style.fontStyle === this.fontStyle.name
      this.fontWeight.state = this.data.style.fontWeight === this.fontWeight.name
      this.color = this.data.style.color
    },
    async saveChanges () {
      await this.saveTableauSurface()
      this.$toaster.info('Sauvegarde effectuée', { timeout: 3000 })
    },
    configFormatDonnees () {
      this.setShowType(!this.showType())
    },
    handleExport () {
      this.$emit('export')
    }
  },
  computed: mapGetters(['selectedCellData']),
  watch: {
    selectedCellData (newValue, oldValue) {
      this.syncStateData(newValue)
    }
  }
}
</script>

<style lang="scss" scoped>
#border-picker {
  position: absolute;
  bottom: 0;
  top: 100%;
  z-index: 999;
  .flex-container {
    box-shadow: 0px 1px 8px 1px rgba(173, 173, 173, 0.66);
    background-color: white;
    border-radius: 3px;
  }
  .flex-item {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    padding: 3px;
    &:hover {
      background-color: #236aa3;
      color: white;
    }
  }
  .flex-item:after {
    content: " ";
    padding-top: 100%;
    display: table;
  }
  .flex-action {
    text-align: center;
    &:hover {
      background-color: #236aa3;
      color: white;
    }
  }
  .border-width-item {
    .line-display {
      width: 80px;
      background-color: black;
    }
    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
    }
    &.active {
      background-color: #236aa3;
      .line-display {
        background-color: white;
      }
    }
  }
}
.action-container {
  position: relative;
  #line-weight {
    margin-left: 5px;
    position: absolute;
    left: 100%;
    top: 0;
    z-index: 999;
  }
  #border-color {
    margin-right: 5px;
    position: absolute;
    right: 100%;
    top: 0;
    z-index: 999;
  }
}

.divider {
  border-right: 1px solid #d1e1ef;
  width: 2px;
  margin: 0 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/tableauSurface/toolbar.vue